import TOML from "smol-toml";
import { MulticastDelegate3, TrainingModelStateFacet } from "flavr-common";
import { TrainingContext } from "../shared/scenarioTypes";
import { Condition } from "../condition/Condition";
import { TrainingObject } from "../scenario/TrainingObject";
import { TrainingScenario } from "../scenario/TrainingScenario";
import { AttachmentChangedEvent, ButtonClickedEvent, ObjectCollisionEvent, StageChangedEvent, TrainingObjectStateChangedEvent, VideoStateChangedEvent, WearableStateChangedEvent, WearableStateChangeFailedEvent } from "../scenario/TrainingEvent";
import { TrainingStage } from "../scenario/TrainingStage";
import { StateNode } from "../shared/StateMachine";
import { modelPool, determineModelNameForPrinting, useExternalMessages } from "@modules/model";
import { TrainingSetting } from "../scenario/TrainingSetting";
import { busService } from "../../bus";
import { TrainingStageCondition } from "../scenario/TrainingStageCondition";
import { ConditionVerificationParser } from "../condition/ConditionVerificationParser";
import { TrainingSequence } from "../scenario/TrainingSequence";
import { PreConditionStateParser } from "../condition/PreConditionStateParser";
const builtInObjects = ["HEAD", "LEFT_HAND", "RIGHT_HAND", "STAGE"];
export class ScenarioFactory {
    constructor() {
        this.scenarioModelUids = [];
        this.onScenarioBuiltDelegate = new MulticastDelegate3();
        this.modelNameToUidMap = new Map();
        this.uidToPositionMap = new Map();
        this.uidToModelStateFacet = new Map();
        this.uidToWearableFacet = new Map();
        this.predefinedTrainingObjects = [];
        this.predefinedObjectCallbacks = new Map();
    }
    /// Getter functions ///
    getPredefinedObjectCallbacks(objectId) {
        return this.predefinedObjectCallbacks.get(objectId);
    }
    getInputConfig() {
        return this.scenarioInputConfig;
    }
    getScenarioModelUids() {
        return this.scenarioModelUids;
    }
    /// Collect data to build the scenario ///
    setToml(fileContents) {
        console.debug("[training] Set TOML File");
        this.scenarioDescription = TOML.parse(fileContents);
    }
    addModel(uid) {
        let modelUID = uid;
        const model = modelPool.getModel(uid);
        if (model === undefined) {
            console.warn(`[training] Cannot find ModelStateFacet for ${determineModelNameForPrinting(uid)}`);
            return;
        }
        let modelName = model.getName();
        if (modelName.includes("$")) {
            const parent = model.getParentOrThrow();
            modelName = parent.getName();
            modelUID = parent.getUID();
        }
        if (modelName === undefined) {
            console.warn(`[training] Model ModelStateFacet with id ${uid} has no associated model name`);
            return;
        }
        this.modelNameToUidMap.set(modelName, modelUID);
    }
    addModelTransformation(uid, worldPosition) {
        this.uidToPositionMap.set(uid, worldPosition);
    }
    addModelStateFacet(uid, facet) {
        this.uidToModelStateFacet.set(uid, facet);
        // The addModel method is only called for models with meshes, however some scenario objects might have no mesh
        // therefore we also need to add models with TrainingModelStateFacets to be safe
        this.addModel(uid);
    }
    addWearableFacet(uid, facet) {
        this.uidToWearableFacet.set(uid, facet);
        // The addModel method is only called for models with meshes, however some scenario objects might have no mesh
        // therefore we also need to add models with TrainingWearableFacets to be safe
        this.addModel(uid);
    }
    addPredefinedScenarioObject(name, states, uid, onStateChanged) {
        var _a;
        const position = (uid !== undefined)
            ? this.uidToPositionMap.get(uid)
            : undefined;
        const modelStates = states.map(state => {
            return { stateName: state };
        });
        const facet = new TrainingModelStateFacet(modelStates, false);
        let currentRoom = modelPool.getCurrentRoom();
        if (currentRoom === undefined) {
            console.warn(`[training] Cannot find current room`);
            currentRoom = modelPool.getRoot();
        }
        const messages = [];
        if (uid === undefined) {
            const model = modelPool.createModel(name, currentRoom, useExternalMessages(messages));
            model.setFacet(facet, useExternalMessages(messages));
            uid = model.getUID();
        }
        else {
            const model = modelPool.getModel(uid);
            model === null || model === void 0 ? void 0 : model.setFacet(facet, useExternalMessages(messages));
        }
        busService.connection("ScenarioFactory").sendMultiple(messages);
        // Store the facet so createStates can find it later
        this.uidToModelStateFacet.set(uid, facet);
        this.predefinedTrainingObjects.push({
            id: name,
            uid: uid !== null && uid !== void 0 ? uid : -1,
            position,
            states
        });
        // Register callback for state changes
        if (onStateChanged !== undefined) {
            if (!this.predefinedObjectCallbacks.has(name)) {
                this.predefinedObjectCallbacks.set(name, []);
            }
            (_a = this.predefinedObjectCallbacks.get(name)) === null || _a === void 0 ? void 0 : _a.push(onStateChanged);
        }
    }
    /// Actually building the scenario ///
    build() {
        var _a, _b;
        console.debug("[training] Start building scenario");
        if (this.scenarioDescription === undefined) {
            console.debug("[training] No scenario description found");
            return;
        }
        if (!this.validateScenarioDescription()) {
            console.error("[training] Scenario description validation failed. Aborting build.");
            return;
        }
        // Parse scenario metadata
        const name = this.scenarioDescription.name;
        const author = this.scenarioDescription.author;
        // Create settings
        const settings = this.createSettings(this.scenarioDescription.settings);
        console.debug(settings);
        // Create stages
        const stages = this.createStages(this.scenarioDescription.stages);
        // Create sequences
        const sequences = this.createSequences(this.scenarioDescription.sequences, stages);
        // Add additional stage FINAL
        stages.push(new TrainingStage("FINAL", "Finished Scenario!", true));
        // Create objects
        const objects = Object
            .entries((_a = this.scenarioDescription.objects) !== null && _a !== void 0 ? _a : {})
            .map(([objectId, object]) => {
            var _a;
            const objectName = object.name;
            let uid = (_a = this.resolveSceneModelUid(objectName)) !== null && _a !== void 0 ? _a : -1;
            let position = this.uidToPositionMap.get(uid);
            // Fill in predefined object data if available
            // combine both object in TOML and predefined object data
            const predefinedTrainignObject = this.predefinedTrainingObjects.find((predefObj) => predefObj.id === objectId);
            if (predefinedTrainignObject !== undefined) {
                object.name = object.name === undefined ? predefinedTrainignObject.id : object.name;
                object.states = object.states === undefined ? predefinedTrainignObject.states : object.states;
                uid = uid === -1 ? predefinedTrainignObject.uid : uid;
                position = position === undefined ? predefinedTrainignObject.position : position;
            }
            return this.createSceneObject(uid, objectId, object, position);
        });
        this.addPredefinedScenarioObject("STAGE", stages.map(stage => stage.id));
        // Create predefined objects
        for (const predefinedTrainingObject of this.predefinedTrainingObjects) {
            if (objects.find(obj => obj.id === predefinedTrainingObject.id) !== undefined) {
                continue;
            }
            const tomlObject = {
                name: predefinedTrainingObject.id,
                states: predefinedTrainingObject.states
            };
            const obj = this.createSceneObject(predefinedTrainingObject.uid, predefinedTrainingObject.id, tomlObject, predefinedTrainingObject.position);
            console.log("Added predefined scenario object", obj);
            objects.push(obj);
        }
        this.scenarioModelUids = objects.map(object => object.modelUid);
        const trainingContext = new TrainingContext();
        // Create events
        const events = Object
            .entries((_b = this.scenarioDescription.events) !== null && _b !== void 0 ? _b : {})
            .map(([eventId, event]) => this.createEvent(eventId, event, trainingContext));
        const scenario = new TrainingScenario(name, author, stages, sequences, objects, events);
        const startStageId = this.scenarioDescription.start;
        const startStage = this.createStageStateMachine(stages, this.scenarioDescription.stages, startStageId);
        if (startStage !== undefined) {
            scenario.setStartNode(startStage.startNode);
            scenario.setAllStates(startStage.allNodes);
        }
        this.scenarioInputConfig = this.parseInputConfig();
        this.onScenarioBuiltDelegate.invoke(scenario, trainingContext, settings);
    }
    /// Helper methods ///
    createSettings(settingsToml) {
        const trainingSettings = [];
        const settingsBlocks = Object.entries(settingsToml !== null && settingsToml !== void 0 ? settingsToml : {});
        settingsBlocks.forEach(([settingId, tomlSetting]) => {
            const tomlSettingTable = tomlSetting;
            Object.keys(tomlSettingTable).forEach(settingName => {
                const setting = this.createSetting(settingId, settingName, tomlSettingTable);
                trainingSettings.push(setting);
            });
        });
        return trainingSettings;
    }
    createStages(stagesToml) {
        return Object
            .entries(stagesToml !== null && stagesToml !== void 0 ? stagesToml : {})
            .map(([stageId, stage]) => this.createStage(stageId, stage, stage.isFinal, stage.isError, stage.errorText));
    }
    createSequences(sequencesToml, stages) {
        return Object
            .entries(sequencesToml !== null && sequencesToml !== void 0 ? sequencesToml : {})
            .map(([sequenceId, sequence]) => this.createSequence(sequenceId, sequence, stages));
    }
    createStageStateMachine(stages, stagesToml, startStageId) {
        var _a;
        if (stages.length === 0)
            return undefined;
        // Wrap stages into state nodes
        const stageNodes = stages.map(stage => new StateNode(stage));
        const stagesMap = new Map(stageNodes.map(node => [node.data.id, node]));
        // Add transitions to stage nodes
        Object
            .entries(stagesToml !== null && stagesToml !== void 0 ? stagesToml : {})
            .forEach(([stageId, stage]) => {
            const transitions = (stage.transitions === undefined)
                ? {}
                : stage.transitions;
            const startStage = stagesMap.get(stageId);
            if (startStage === undefined)
                return;
            // A stage is final if it has no post condition
            startStage.data.isFinal = Object.keys(transitions).length === 0;
            Object.entries(transitions)
                .forEach(([nextState, eventName]) => {
                const nextStage = stagesMap.get(nextState);
                if (nextStage === undefined) {
                    console.warn(`[training] Next stage "${nextState}" not found`);
                    return;
                }
                if (typeof eventName === "string") {
                    const condition = new TrainingStageCondition(eventName);
                    startStage.addTransition(nextStage, condition);
                }
                else if (Array.isArray(eventName)) {
                    eventName.forEach((singleEventName) => {
                        const condition = new TrainingStageCondition(singleEventName);
                        startStage.addTransition(nextStage, condition);
                    });
                }
            });
        });
        const startNode = (_a = (startStageId !== undefined ? stagesMap.get(startStageId) : undefined)) !== null && _a !== void 0 ? _a : stageNodes[0];
        if (startStageId !== undefined && !stagesMap.has(startStageId))
            console.warn(`[training] Start stage "${startStageId}" not found, falling back to first stage`);
        return { startNode, allNodes: stageNodes };
    }
    createSetting(settingId, settingName, tomlSetting) {
        return new TrainingSetting(settingId, settingName, tomlSetting[settingName]);
    }
    createStage(stageId, tomlStage, isFinal, isError, errorText) {
        return new TrainingStage(stageId, tomlStage.label, isFinal, isError, errorText);
    }
    createSequence(sequenceId, tomlSequence, stages) {
        const trainingStages = [];
        for (const stageId of tomlSequence.stages) {
            const trainingStage = stages.find(stage => stage.id === stageId);
            if (trainingStage !== undefined) {
                trainingStages.push(trainingStage);
            }
        }
        const preConditionString = tomlSequence.preCondition;
        if (preConditionString === undefined) {
            console.error("[training]", `Sequence "${sequenceId}" has no pre-condition defined. Pre-conditions are mandatory`);
        }
        const preCondition = PreConditionStateParser.parse(preConditionString !== null && preConditionString !== void 0 ? preConditionString : "");
        return new TrainingSequence(sequenceId, tomlSequence.name, trainingStages, preCondition.status ? preCondition.value : []);
    }
    matchesSceneModelName(sceneModelName, requestedModelName) {
        if (sceneModelName === requestedModelName) {
            return true;
        }
        const childNameSeparatorIndex = sceneModelName.lastIndexOf("$");
        if (childNameSeparatorIndex === -1) {
            return false;
        }
        return sceneModelName.slice(childNameSeparatorIndex + 1) === requestedModelName;
    }
    findSceneModelByName(modelName) {
        var _a;
        const startModel = (_a = modelPool.getCurrentRoom()) !== null && _a !== void 0 ? _a : modelPool.getRoot();
        const modelQueue = [startModel];
        while (modelQueue.length > 0) {
            const currentModel = modelQueue.shift();
            if (currentModel === undefined) {
                continue;
            }
            if (this.matchesSceneModelName(currentModel.getName(), modelName)) {
                return currentModel;
            }
            currentModel.getChildren().forEach(child => modelQueue.push(child));
        }
        return undefined;
    }
    resolveSceneModelUid(modelName) {
        var _a;
        const modelUid = this.modelNameToUidMap.get(modelName);
        if (modelUid !== undefined) {
            return modelUid;
        }
        // An empty name would match every sub model whose name ends with "$" (e.g. "tuer_0$")
        if (modelName.trim() === "") {
            return undefined;
        }
        return (_a = this.findSceneModelByName(modelName)) === null || _a === void 0 ? void 0 : _a.getUID();
    }
    createSceneObject(uid, objectId, object, position) {
        var _a, _b;
        const objectStates = object.states;
        let states = (objectStates !== undefined)
            ? this.createStates(uid)
            : [];
        // Fallback for objects with no scene model (e.g. input-only objects like HasBeard):
        // if TOML defines states but no TrainingModelStateFacet was found for the uid, use the TOML states directly.
        if (states.length === 0 && objectStates !== undefined && objectStates.length > 0) {
            states = objectStates.map(name => ({ name }));
        }
        const transitions = (object.transitions !== undefined)
            ? object.transitions
                .map((transitionText) => this.parseTransition(transitionText))
                .filter(trans => trans !== undefined) // Ensure no undefined values are left
            : [];
        const modelName = object.name;
        // Colliders can only be copied from state models - if no state has its own model, the object keeps its own collider
        const hasStateModels = states.some(state => state.modelUid !== undefined);
        const copyCollider = hasStateModels && ((_b = (_a = this.uidToModelStateFacet.get(uid)) === null || _a === void 0 ? void 0 : _a.copyCollider) !== null && _b !== void 0 ? _b : false);
        return new TrainingObject(objectId, uid, modelName, states, transitions, position, copyCollider);
    }
    createStates(uid) {
        const modelStateFacet = this.uidToModelStateFacet.get(uid);
        if (modelStateFacet === undefined)
            return [];
        return modelStateFacet.states.map(facet => {
            return {
                name: facet.stateName,
                modelName: facet.modelName,
                modelUid: (facet.modelName !== undefined)
                    ? this.resolveSceneModelUid(facet.modelName)
                    : undefined
            };
        });
    }
    createEvent(eventId, event, trainingContext) {
        const trainingEvent = this.createEventByType(eventId, event, trainingContext);
        // Parse icon if provided
        if (event.icon !== undefined) {
            const icon = event.icon;
            const url = icon.url;
            const target = icon.target;
            if (url !== undefined && target !== undefined) {
                Array.isArray(trainingEvent.value) ? trainingEvent.value[0].icon = { url, target } : trainingEvent.value.icon = { url, target };
            }
            else {
                console.warn(`[training] An icon was provided for ${eventId} but its either missing an 'url' and/or 'target'`);
            }
        }
        return trainingEvent;
    }
    createEventByType(eventId, event, trainingContext) {
        const eventType = this.parseEventType(event.type);
        const condition = new Condition(event.condition);
        const target = event.target;
        if (Array.isArray(eventType) && eventType.length > 0) {
            // Multi event type
            const multiEvents = eventType.map((type) => this.createTrainingEvent(eventId, type, trainingContext));
            return { condition, value: multiEvents, target };
        }
        return { condition, value: this.createTrainingEvent(eventId, eventType, trainingContext), target };
    }
    createTrainingEvent(eventId, eventType, trainingContext) {
        switch (eventType) {
            case "CONTROLLER_ACTION_CLICK":
                return new ButtonClickedEvent(eventId, trainingContext);
            case "OBJECT_COLLISION":
                return new ObjectCollisionEvent(eventId, trainingContext);
            case "ATTACHMENT_CHANGED":
                return new AttachmentChangedEvent(eventId, trainingContext);
            case "STAGE_CHANGED":
                return new StageChangedEvent(eventId, trainingContext);
            case "VIDEO_STATE_CHANGED":
                return new VideoStateChangedEvent(eventId, trainingContext);
            case "WEARABLE_STATE_CHANGED":
                return new WearableStateChangedEvent(eventId, trainingContext);
            case "WEARABLE_STATE_CHANGE_FAILED":
                return new WearableStateChangeFailedEvent(eventId, trainingContext);
            case "OBJECT_STATE_CHANGED":
                return new TrainingObjectStateChangedEvent(eventId, trainingContext);
            default:
                throw new Error(`Unrecognized event type ${String(eventType)}`);
        }
    }
    /// Parsing methods ///
    parseTransition(transitionText) {
        const re = /^([a-zA-Z0-9_-]+)\s*\|\s*([a-zA-Z0-9_-]+)\s*->\s*([a-zA-Z0-9_-]+)/;
        const match = transitionText.match(re);
        if (match === null || match.length !== 4) {
            console.warn(`[training] Transition "${transitionText}" is not a valid transition.\nIt should be <action> | <startState> -> <endState>`);
            return undefined;
        }
        return {
            eventId: match[1],
            startState: match[2],
            endState: match[3]
        };
    }
    parseEventType(eventType) {
        if (Array.isArray(eventType)) {
            return eventType.map(typeStr => this.parseEventType(typeStr));
        }
        switch (eventType) {
            case "CONTROLLER_ACTION_CLICK":
                return "CONTROLLER_ACTION_CLICK";
            case "OBJECT_COLLISION":
                return "OBJECT_COLLISION";
            case "ATTACHMENT_CHANGED":
                return "ATTACHMENT_CHANGED";
            case "STAGE_CHANGED":
                return "STAGE_CHANGED";
            case "VIDEO_STATE_CHANGED":
                return "VIDEO_STATE_CHANGED";
            case "WEARABLE_STATE_CHANGED":
                return "WEARABLE_STATE_CHANGED";
            case "WEARABLE_STATE_CHANGE_FAILED":
                return "WEARABLE_STATE_CHANGE_FAILED";
            case "OBJECT_STATE_CHANGED":
                return "OBJECT_STATE_CHANGED";
            case "TIMER_EXPIRED":
                return "TIMER_EXPIRED";
            default:
                throw new Error(`[training] Unrecognized event type ${eventType}`);
        }
    }
    parseInputConfig() {
        var _a, _b;
        const inputToml = (_a = this.scenarioDescription) === null || _a === void 0 ? void 0 : _a.input;
        if (inputToml === undefined)
            return undefined;
        const title = inputToml.title;
        const text = inputToml.text;
        const entries = [];
        for (const [id, entryValue] of Object.entries(inputToml)) {
            if (id === "text" || id === "title")
                continue;
            const entry = entryValue;
            const typeStr = entry.type;
            if (typeStr === "Checkbox") {
                const defaultVal = entry.default;
                entries.push({
                    id,
                    label: entry.label,
                    type: "Checkbox",
                    default: typeof defaultVal === "boolean" ? defaultVal : false
                });
            }
            else if (typeStr === "Select") {
                const options = entry.options;
                const defaultVal = (_b = entry.default) !== null && _b !== void 0 ? _b : options[0];
                entries.push({ id, label: entry.label, type: "Select", options, default: defaultVal });
            }
        }
        return { title, text, entries };
    }
    /// Verification / Validation methods ///
    validateScenarioDescription() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
        const scenarioDescription = this.scenarioDescription;
        if (scenarioDescription === undefined)
            return false;
        // Extract start stage
        const startStage = (_a = scenarioDescription.start) !== null && _a !== void 0 ? _a : undefined;
        // Perform validations
        let isValid = true;
        const logError = (message) => {
            console.error(`[training] ${message}`);
            isValid = false;
        };
        const logWarning = (message) => {
            console.warn(`[training] ${message}`);
        };
        if (startStage === undefined)
            logError("No start stage defined in scenario.");
        // Collect all object ids
        const objectsTable = ((_b = scenarioDescription.objects) !== null && _b !== void 0 ? _b : {});
        // Set of all object ids
        const objectIds = new Set(Object.keys(objectsTable));
        // Set of predefined object ids
        const predefinedObjectIds = new Set();
        builtInObjects.forEach((objectId) => objectIds.add(objectId));
        // Add predefined training objects
        this.predefinedTrainingObjects.forEach(predef => {
            objectIds.add(predef.id);
            predefinedObjectIds.add(predef.id);
        });
        // Collect input object ids (non-button fields that map to scene objects, excluded from reachability checks)
        const inputObjectIds = new Set();
        const inputTomlForValidation = ((_c = scenarioDescription.input) !== null && _c !== void 0 ? _c : {});
        for (const [id, entryValue] of Object.entries(inputTomlForValidation)) {
            if (id === "text" || id === "title")
                continue;
            const entry = entryValue;
            const typeStr = entry.type;
            if (id !== "START_AS_SPECTATOR" && id !== "START_IN_VR" && typeStr !== "Button") {
                inputObjectIds.add(id);
            }
        }
        // 1. & 2. Check whether all stage-transitions lead to an existing stage
        const stageIds = Object.keys((_d = scenarioDescription.stages) !== null && _d !== void 0 ? _d : {});
        if (stageIds.length === 0) {
            logError("Scenario has no stages defined.");
        }
        const allStageIds = [...stageIds, "FINAL"];
        const adjacency = new Map(allStageIds.map(id => [id, []]));
        for (const [stageId, stage] of Object.entries((_e = scenarioDescription.stages) !== null && _e !== void 0 ? _e : {})) {
            if (typeof stage.label !== "string")
                logError(`Stage "${stageId}" is missing a "label".`);
            for (const [nextStage, eventName] of Object.entries((_f = stage.transitions) !== null && _f !== void 0 ? _f : {})) {
                if (!allStageIds.includes(nextStage)) {
                    logError(`Stage "${stageId}" has a transition to a non-existent stage: "${nextStage}".`);
                }
                if (Array.isArray(eventName)) {
                    eventName.forEach((singleEventName) => {
                        var _a;
                        if (typeof singleEventName !== "string" || !((_a = scenarioDescription.events) === null || _a === void 0 ? void 0 : _a[singleEventName])) {
                            logError(`Stage "${stageId}" transition refers to a non-existent event: "${singleEventName}".`);
                        }
                    });
                }
                else if (typeof eventName !== "string" || ((_g = scenarioDescription.events) === null || _g === void 0 ? void 0 : _g[eventName]) === undefined) {
                    logError(`Stage "${stageId}" transition refers to a non-existent event: "${String(eventName)}".`);
                }
                (_h = adjacency.get(stageId)) === null || _h === void 0 ? void 0 : _h.push(nextStage);
            }
        }
        // Depth-first search to find reachable stages
        const depthFirstSearch = (start) => {
            var _a;
            const visited = new Set();
            const stack = [start];
            while (stack.length > 0) {
                const cur = stack.pop();
                if (visited.has(cur))
                    continue;
                visited.add(cur);
                (_a = adjacency.get(cur)) === null || _a === void 0 ? void 0 : _a.forEach(n => {
                    if (!visited.has(n))
                        stack.push(n);
                });
            }
            return visited;
        };
        // Check reachability from start stage to all other stages and from all stages to FINAL
        // Check reachability from start stage
        if (stageIds.length > 0) {
            const startStageId = startStage;
            const reachableFromStart = depthFirstSearch(startStageId);
            for (const stageId of stageIds) {
                if (!reachableFromStart.has(stageId)) {
                    logError(`Stage "${stageId}" is not reachable from start stage "${startStageId}".`);
                }
            }
            // Check reachability to FINAL stage
            for (const stageId of stageIds) {
                const reachableFromCurrent = depthFirstSearch(stageId);
                if (!reachableFromCurrent.has("FINAL")) {
                    logError(`"FINAL" stage is not reachable from stage "${stageId}".`);
                }
            }
        }
        // check if error stages do not have a pre-condition
        for (const [stageId, stage] of Object.entries((_j = scenarioDescription.stages) !== null && _j !== void 0 ? _j : {})) {
            if (stage.isError === true && stage.preCondition !== undefined) {
                logError(`Stage "${stageId}" is marked as an error stage but has a pre-condition defined. Error stages should not have pre-conditions.`);
            }
            if (stage.isError === true && stage.errorText === undefined) {
                logWarning(`Error stage "${stageId}" is missing an "errorText".`);
            }
            else if (stage.isError === true && typeof stage.errorText !== "string") {
                logError(`Error stage "${stageId}" has an "errorText" that is not a string.`);
            }
        }
        // 3. Check event conditions for valid object references
        // Build map of object states
        const objectStatesMap = new Map();
        // Add object states from scenario description
        for (const [objectId, object] of Object.entries(objectsTable)) {
            const scenarioObject = object;
            const objectStates = (_k = scenarioObject === null || scenarioObject === void 0 ? void 0 : scenarioObject.states) !== null && _k !== void 0 ? _k : [];
            objectStatesMap.set(objectId, objectStates);
        }
        this.predefinedTrainingObjects.forEach(predef => {
            objectStatesMap.set(predef.id, predef.states);
        });
        // Handle Stages as special object with states being the stage ids
        objectStatesMap.set("STAGE", stageIds);
        // Validate event conditions
        for (const [eventId, event] of Object.entries((_l = scenarioDescription.events) !== null && _l !== void 0 ? _l : {})) {
            if (typeof event.condition !== "string") {
                logError(`Event "${eventId}" is missing a "condition".`);
                continue;
            }
            const conditionText = event.condition;
            // Parse condition and extract object references
            const conditionVerificationParserResult = ConditionVerificationParser.parse(conditionText);
            if (conditionVerificationParserResult.status && conditionVerificationParserResult.value.length !== 0) {
                for (const objectRef of conditionVerificationParserResult.value) {
                    if (!objectIds.has(objectRef.objectName)) {
                        logError(`Event "${eventId}" condition refers to a non-existent object: "${objectRef.objectName}".`);
                    }
                    if (objectRef.stateName !== undefined) {
                        const objectStates = (_m = objectStatesMap.get(objectRef.objectName)) !== null && _m !== void 0 ? _m : [];
                        if (!objectStates.includes(objectRef.stateName)) {
                            logError(`Event "${eventId}" condition refers to a non-existent state "${objectRef.stateName}" for object: "${objectRef.objectName}".`);
                        }
                    }
                }
            }
            else {
                logError(`Event "${eventId}" has an invalid condition: "${conditionText}".`);
            }
        }
        // Validate event targets
        const targetUnsupportedTypes = new Set(["CONTROLLER_ACTION_CLICK", "STAGE_CHANGED", "TIMER_EXPIRED"]);
        for (const [eventId, event] of Object.entries((_o = scenarioDescription.events) !== null && _o !== void 0 ? _o : {})) {
            if (event.target === undefined)
                continue;
            const types = Array.isArray(event.type) ? event.type : [event.type];
            for (const type of types) {
                if (targetUnsupportedTypes.has(type)) {
                    logError(`Event "${eventId}" specifies a "target" but type "${type}" does not support target filtering.`);
                }
            }
        }
        // Helper function to disassemble object transition strings
        const disassembleObjectTransition = (transition) => {
            const eventAndTransition = transition.split("|");
            if (eventAndTransition.length !== 2)
                return undefined;
            const eventId = eventAndTransition[0].trim();
            const states = eventAndTransition[1].split("->");
            if (states.length !== 2)
                return undefined;
            const startState = states[0].trim();
            const endState = states[1].trim();
            return [eventId, startState, endState];
        };
        // 4. Check object states for reachability
        // Validate object transitions
        for (const [objectId, object] of Object.entries(objectsTable !== null && objectsTable !== void 0 ? objectsTable : {})) {
            const scenarioObject = object;
            const objectStates = (_p = objectStatesMap.get(objectId)) !== null && _p !== void 0 ? _p : [];
            // Collect used states from transitions
            const usedStates = new Set();
            for (const transitionString of ((_q = scenarioObject.transitions) !== null && _q !== void 0 ? _q : [])) {
                const disassembledTransition = disassembleObjectTransition(transitionString);
                if (disassembledTransition === undefined) {
                    logError(`Object "${objectId}" has an invalid transition: "${transitionString}".`);
                    continue;
                }
                const [eventId, startState, endState] = disassembledTransition;
                // Validate event and states of the transition of the object
                if (((_r = scenarioDescription.events) === null || _r === void 0 ? void 0 : _r[eventId]) === undefined) {
                    logError(`Object "${objectId}" transition refers to a non-existent event: "${eventId}".`);
                }
                if (!objectStates.includes(startState)) {
                    logError(`Object "${objectId}" transition starts from a non-existent state: "${startState}".`);
                }
                if (!objectStates.includes(endState)) {
                    logError(`Object "${objectId}" transition ends in a non-existent state: "${endState}".`);
                }
                usedStates.add(startState);
                usedStates.add(endState);
            }
            // Check for unreachable states (input objects are excluded — their states are set by the dialog)
            for (const state of objectStates) {
                if (!usedStates.has(state) && !predefinedObjectIds.has(objectId) && !inputObjectIds.has(objectId)) {
                    logWarning(`Object "${objectId}" state "${state}" is not reachable via any event transition. This might be fine if the model state is changed externally.`);
                }
            }
        }
        // 5. Validate scenario sequences for correct stage references and error stages
        for (const [sequenceId, sequence] of Object.entries((_s = scenarioDescription.sequences) !== null && _s !== void 0 ? _s : {})) {
            if (sequence.preCondition === undefined) {
                logError(`Sequence "${sequenceId}" is missing a pre-condition. Each sequence must have a pre-condition defined.`);
            }
            else {
                const preConditionParseResult = PreConditionStateParser.parse(sequence.preCondition);
                if (!preConditionParseResult.status) {
                    logError(`Sequence "${sequenceId}" has an invalid pre-condition: "${sequence.preCondition}".`);
                }
            }
            const sequenceStageIds = sequence.stages;
            for (const stageId of sequenceStageIds) {
                if (!stageIds.includes(stageId)) {
                    logError(`Sequence "${sequenceId}" refers to a non-existent stage: "${stageId}".`);
                }
                // check if stage is error stage
                const stagesToml = ((_t = scenarioDescription.stages) !== null && _t !== void 0 ? _t : {});
                const stage = ((_u = stagesToml[stageId]) !== null && _u !== void 0 ? _u : {});
                if (stage === undefined)
                    continue;
                if (stage.isError === true) {
                    logError(`Sequence "${sequenceId}" includes stage "${stageId}" which is marked as an error stage.`);
                }
            }
        }
        // 6, verify settings
        const settingsTable = ((_v = scenarioDescription.settings) !== null && _v !== void 0 ? _v : {});
        for (const settingId of Object.keys(settingsTable)) {
            if (settingId !== "general") {
                if (!stageIds.includes(settingId)) {
                    logError(`Setting "${settingId}" refers to a non-existent stage: "${settingId}". Each setting block must refer to an existing stage.`);
                }
            }
        }
        // 7. Validate [input] section
        for (const [id, entryValue] of Object.entries(inputTomlForValidation)) {
            if (id === "text" || id === "title")
                continue;
            const entry = entryValue;
            const typeStr = entry.type;
            if (id === "START_AS_SPECTATOR" || id === "START_IN_VR" || typeStr === "Button")
                continue;
            if (!objectIds.has(id)) {
                logError(`Input field "${id}" has no matching object definition in [objects.${id}].`);
                continue;
            }
            const fieldStates = (_w = objectStatesMap.get(id)) !== null && _w !== void 0 ? _w : [];
            if (typeStr === "Checkbox") {
                if (!fieldStates.includes("false") || !fieldStates.includes("true")) {
                    logError(`Object "${id}" for Checkbox input must have states "false" and "true".`);
                }
                const defaultVal = entry.default;
                if (defaultVal !== undefined && typeof defaultVal !== "boolean") {
                    logError(`Input field "${id}" (Checkbox) has a non-boolean default. Use "default = true" or "default = false".`);
                }
            }
            else if (typeStr === "Select") {
                const options = entry.options;
                if (options === undefined || options.length === 0) {
                    logError(`Input field "${id}" (Select) must have at least one entry in "options".`);
                    continue;
                }
                for (const option of options) {
                    if (!fieldStates.includes(option)) {
                        logError(`Input field "${id}" option "${option}" is not present in the object's states.`);
                    }
                }
                const defaultVal = entry.default;
                if (defaultVal !== undefined && !options.includes(defaultVal)) {
                    logError(`Input field "${id}" default "${defaultVal}" is not one of the options.`);
                }
            }
            else if (typeStr === undefined) {
                logError(`Input field "${id}" is missing a "type". Valid types are "Checkbox", "Select", "Button".`);
            }
            else {
                logError(`Input field "${id}" has unknown type "${typeStr}". Valid types are "Checkbox", "Select", "Button".`);
            }
        }
        console.log("[training]", isValid ? "Scenario description (TOML file) is formally correct." : "Scenario description is invalid.");
        return isValid;
    }
    removeModelsWithoutTomlEntry() {
        const messageArray = [];
        for (const [modelName, uid] of this.modelNameToUidMap.entries()) {
            if (!this.isModelNameInToml(modelName)) {
                messageArray.push(`[training] Model "${modelName}" with uid ${uid} is not present in the TOML and will be removed from the scenario.`);
                this.modelNameToUidMap.delete(modelName);
            }
        }
        if (messageArray.length > 0) {
            console.debug("[training]", messageArray.join("\n"));
        }
    }
    validateTrainingObjects() {
        const errors = [];
        if (this.scenarioDescription === undefined) {
            errors.push("TOML-Inhalt wurde noch nicht geparst. Validierung nicht möglich.");
            return false;
        }
        // Remove models that are not in the toml file but where added by Facet
        this.removeModelsWithoutTomlEntry();
        // Check if all models defined in the toml file are present in the scene after removal of models not in the toml
        if (!this.checkAllModelsFromSceneIncludedInTomlFile()) {
            errors.push("Nicht alle Modelle aus der TOML-Datei sind in der Szene vorhanden.");
        }
        else {
            console.log("[training] All models defined in the TOML file are present in the scene.");
        }
        errors.length !== 0 && console.error("Training", errors);
        return errors.length === 0;
    }
    isModelNameInToml(modelName) {
        if (this.scenarioDescription === undefined) {
            console.warn("[training] Cannot check if model is in TOML because no scenario description is set.");
            return false;
        }
        const objects = this.scenarioDescription.objects;
        if (objects === undefined) {
            return false;
        }
        let found = false;
        Object.entries(objects).forEach(([objectId, object]) => {
            if (objectId.startsWith("VIDEO") || objectId.startsWith("WEARABLE")) {
                // first check for TrainingVideoFactes and TrainingWearableFacets in predefined models
                this.predefinedTrainingObjects.forEach((predefObj) => {
                    if (predefObj.id.endsWith(modelName))
                        found = true;
                });
            }
            else {
                const tomlObjectsTable = object;
                Object.keys(tomlObjectsTable).forEach(objectName => {
                    const objectNameFromToml = tomlObjectsTable[objectName];
                    if (objectName === "name" && objectNameFromToml === modelName)
                        found = true;
                });
            }
        });
        return found;
    }
    checkAllModelsFromSceneIncludedInTomlFile() {
        if (this.scenarioDescription === undefined) {
            console.warn("[training] Cannot check if model is in TOML because no scenario description is set.");
            return false;
        }
        const objects = this.scenarioDescription.objects;
        if (objects === undefined) {
            return false;
        }
        const modelsFromToml = [];
        // Collect all model names from TOML
        Object.entries(objects).forEach(([_objectId, object]) => {
            const tomlObjectsTable = object;
            Object.keys(tomlObjectsTable).forEach(settingName => {
                if (settingName === "name") {
                    modelsFromToml.push(tomlObjectsTable[settingName]);
                }
            });
        });
        // remove prefix VIDEO_ and WEARABLE_ from model names from predefinedTrainingObjects
        const predefinedModelNames = this.predefinedTrainingObjects.map(obj => {
            if (obj.id.startsWith("VIDEO-")) {
                return obj.id.replace("VIDEO-", "");
            }
            else if (obj.id.startsWith("WEARABLE-")) {
                return obj.id.replace("WEARABLE-", "");
            }
            return obj.id;
        });
        // Collect all model names from the current scene
        const modelsInScene = [...Array.from(this.modelNameToUidMap.keys()), ...predefinedModelNames];
        // Check if all models from TOML are present in the scene
        let allModelsPresent = true;
        for (const modelName of modelsFromToml) {
            if (!modelsInScene.includes(modelName) && this.resolveSceneModelUid(modelName) === undefined) {
                console.warn(`[training] Model "${modelName}" is defined in the TOML but not present in the current scene.`);
                allModelsPresent = false;
            }
        }
        // All models are present
        return allModelsPresent;
    }
}
